import { referrerSource } from './referrerSource';

// Reads the first-party attribution cookie (`tt_attr`) set on the apex domain
// `.thisandthat.chat` by the marketing site (and any subdomain) when a visitor
// lands with a utm_source. Because it's an apex cookie it is readable here on
// the assistant app, so a signup can be credited to the campaign that first
// brought the visitor in — even days later, across tabs, and through OAuth
// redirects, which per-tab sessionStorage cannot survive. The cookie is only
// ever written with CookieYes analytics consent (see the marketing BaseLayout
// writer), so reading it requires no separate consent gate.

export interface CookieAttribution {
  source: string;
  medium: string;
  campaign: string;
}

export const readAttributionCookie = (): CookieAttribution | null => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)tt_attr=([^;]+)/);
    if (!match) {
      return null;
    }
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    if (!parsed || typeof parsed.s !== 'string' || !parsed.s) {
      return null;
    }
    return {
      source: parsed.s,
      medium: typeof parsed.m === 'string' ? parsed.m : '',
      campaign: typeof parsed.c === 'string' ? parsed.c : '',
    };
  } catch {
    return null;
  }
};

export const readFirstTouchAttribution = (): { source: string } | null => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)tt_attr=([^;]+)/);
    if (!match) return null;
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    if (!parsed || typeof parsed.fs !== 'string' || !parsed.fs) return null;
    return { source: parsed.fs };
  } catch {
    return null;
  }
};

export interface CookieAdAttribution {
  /** Meta ad click id. */
  fbclid: string | null;
  /** When that click happened, in ms. Meta's `fbc` cannot be built without it. */
  fbclidAt: number | null;
  /** Meta's browser id, copied from its own `_fbp` cookie on the marketing site. */
  fbp: string | null;
  /** Whether advertising consent was granted, as recorded on the marketing site. */
  adsConsentGranted: boolean;
}

/**
 * Reads the Meta advertising fields the marketing site records at landing.
 *
 * These cannot be read directly here: Meta scopes `_fbp` and `_fbc` to
 * www.thisandthat.chat and they are not visible on this subdomain (verified
 * 2026-07-31). The same is true of the consent decision — CookieYes keeps a
 * separate record per domain, and this app's own record reports
 * advertisement:no even when the marketing site has yes. So both ride in
 * `tt_attr`, which is set on the apex and does cross.
 *
 * `adsConsentGranted` defaults to false when absent, so a cookie written before
 * this field existed can never be mistaken for consent.
 */
export const readAdAttribution = (): CookieAdAttribution | null => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)tt_attr=([^;]+)/);
    if (!match) return null;
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    if (!parsed) return null;
    return {
      fbclid: typeof parsed.fbclid === 'string' ? parsed.fbclid : null,
      fbclidAt: typeof parsed.fbclidAt === 'number' ? parsed.fbclidAt : null,
      fbp: typeof parsed.fbp === 'string' ? parsed.fbp : null,
      adsConsentGranted: parsed.ads === 1,
    };
  } catch {
    return null;
  }
};

const APEX_COOKIE_DOMAIN = '.thisandthat.chat';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const hasAnalyticsConsent = (): boolean => {
  const match = document.cookie.match(/cookieyes-consent=([^;]+)/);
  if (!match) return false;
  return /analytics:yes/.test(decodeURIComponent(match[1]));
};

// How this visit arrived, held for the tab's session until consent allows writing it.
const REFERRER_STASH_KEY = 'tt_referrer_source';
// No source: an internal or unparseable referrer. Stored so a later load in the
// same tab, such as the return from Google sign-in, cannot claim the slot.
const NO_REFERRER_SOURCE = '';
// The names CookieYes has used for its consent-change event. The marketing
// site's BaseLayout listens for the same three.
const CONSENT_UPDATE_EVENTS = [
  'cookieyes_consent_update',
  'cky_consent_update',
  'cookieyesUpdated',
] as const;

/**
 * Remembers how this visit arrived, before the first route change replaces
 * `document.referrer`. Only the tab's first load counts: sessionStorage survives
 * an OAuth round trip, whose return would otherwise read as a visit from
 * accounts.google.com. Holds a channel label in this origin's session storage
 * and writes no cookie, so it needs no consent, like `captureUtmFromUrl`.
 */
export const stashReferrerSource = (): void => {
  try {
    if (sessionStorage.getItem(REFERRER_STASH_KEY) !== null) return;
    sessionStorage.setItem(
      REFERRER_STASH_KEY,
      referrerSource(document.referrer) ?? NO_REFERRER_SOURCE,
    );
  } catch {
    // Attribution is never worth breaking a page load over.
  }
};

/**
 * Writes the stashed source as the first touch, once analytics consent exists.
 *
 * Writes the same apex `tt_attr` cookie the marketing site owns, so the existing
 * read path picks it up with no other changes, and only ever fills `fs` when it is
 * absent. It never touches `s`: last touch means "which of our pages sent the
 * click", and someone who never saw our pages has no last touch to record.
 *
 * Gated on analytics consent to preserve the invariant this module documents —
 * that the cookie is only ever written with consent, which is what lets every
 * reader skip its own consent check.
 */
export const writeStashedFirstTouch = (): void => {
  try {
    if (!hasAnalyticsConsent()) return;
    if (readFirstTouchAttribution()) return;

    const source = sessionStorage.getItem(REFERRER_STASH_KEY);
    if (!source) return;

    const match = document.cookie.match(/(?:^|;\s*)tt_attr=([^;]+)/);
    const existing = match ? JSON.parse(decodeURIComponent(match[1])) : {};
    const next = {
      ...(existing && typeof existing === 'object' ? existing : {}),
      fs: source,
    };

    // The cookie must be set on the apex domain so the marketing site and this app share one record.
    // biome-ignore lint/suspicious/noDocumentCookie: same synchronous writer as the marketing site
    document.cookie =
      `tt_attr=${encodeURIComponent(JSON.stringify(next))}` +
      `;domain=${APEX_COOKIE_DOMAIN};path=/;max-age=${COOKIE_MAX_AGE_SECONDS}` +
      ';SameSite=Lax;Secure';
  } catch {
    // Attribution is never worth breaking a page load over.
  }
};

/**
 * Records a first touch for visitors who reach this app without ever loading the
 * marketing site — a search result pointing straight at assistant.thisandthat.chat,
 * for instance. Those signups had both attribution columns blank.
 *
 * A new visitor has not answered this domain's consent banner when the app
 * boots (CookieYes keeps a record per domain), so the source is stashed first
 * and written when consent is already there or arrives later in the session.
 */
export const captureFirstTouchFromReferrer = (): void => {
  stashReferrerSource();
  writeStashedFirstTouch();
  for (const event of CONSENT_UPDATE_EVENTS) {
    document.addEventListener(event, writeStashedFirstTouch);
  }
};
